import { DateTime } from "luxon";

// GetSubscriptionResponse.json is a deprecated string field carrying the full raw Zuora
// subscription document. Some values (e.g. payment_status) have no typed proto equivalent yet
// and can only be read from this blob - see getZuoraAccountIdFromRawDoc in
// subscription-details-zuora.tsx for the original precedent of this pattern.
export interface RawSubscriptionDoc {
  zuora?: {
    payment_status?: unknown;
    zuora_subscription_status?: unknown;
    zuora_subscription_status_date?: unknown;
  };
}

export const parseRawSubscriptionDoc = (
  rawDoc: string | undefined,
): RawSubscriptionDoc | undefined => {
  if (!rawDoc) {
    return undefined;
  }
  try {
    return JSON.parse(rawDoc) as RawSubscriptionDoc;
  } catch {
    return undefined;
  }
};

export const getPaymentStatus = (
  rawDoc: string | undefined,
): string | undefined => {
  const paymentStatus = parseRawSubscriptionDoc(rawDoc)?.zuora?.payment_status;
  if (typeof paymentStatus !== "string" || !paymentStatus.trim()) {
    return undefined;
  }
  // Displayed verbatim - trim() above only tests for whitespace-only, it does not alter the
  // returned value.
  return paymentStatus;
};

export interface ZuoraSubscriptionStatus {
  status: string;
  display: string;
}

const formatSubscriptionStatusDate = (dateStr: string): string => {
  const parsed = DateTime.fromISO(dateStr);
  return parsed.isValid ? parsed.toFormat("dd MMM yyyy") : dateStr;
};

// Subscription.SubscriptionStatusDate__c is only meaningful for two of the picklist values -
// "Scheduled to Start" and "Active (Pending Cancellation)" - so `status` (used for chip color
// lookup) and `display` (the constructed label, which may include the date) are returned
// separately.
export const getZuoraSubscriptionStatus = (
  rawDoc: string | undefined,
): ZuoraSubscriptionStatus | undefined => {
  const zuora = parseRawSubscriptionDoc(rawDoc)?.zuora;
  const status = zuora?.zuora_subscription_status;
  if (typeof status !== "string" || !status.trim()) {
    return undefined;
  }

  const statusDate = zuora?.zuora_subscription_status_date;
  const formattedDate =
    typeof statusDate === "string" && statusDate.trim()
      ? formatSubscriptionStatusDate(statusDate)
      : undefined;

  let display = status;
  if (status === "Scheduled to Start" && formattedDate) {
    display = `Scheduled to start ${formattedDate}`;
  } else if (status === "Active (Pending Cancellation)" && formattedDate) {
    display = `Active (Pending cancellation on ${formattedDate})`;
  }

  return { status, display };
};
